import axios from "axios";
import * as Api from "~/generated/api";
import { Configuration } from "~/generated/configuration";
import Cookies from "js-cookie";
import { useAuthStore } from "~/store/auth-store";
import { handleError } from "vue";
import SidebarItem from "~/pages/admin/sidebarItem.vue";
class ApiManager {
  private static instance: ApiManager;
  private config: Configuration;
  private refreshInFlight: Promise<boolean> | null = null;

  public apis: { [key: string]: any } = {};

  private constructor(
    config?: { public?: { apiBase?: string }; apiBase?: string } | null,
  ) {
    const initialAccessToken = Cookies.get("accessToken") || "";
    const initialBasePath = ApiManager.resolveApiBase(config);

    const getApiPath = (): string => {
      const protocol = window.location.protocol;
      const host = window.location.host;
      return initialBasePath && initialBasePath !== "/"
        ? initialBasePath
        : `${protocol}//${host}`;
    };

    const apiPath = getApiPath();

    console.log("initialAccessToken", initialAccessToken);
    console.log("initialBasePath", apiPath);

    this.config = new Configuration({
      accessToken: initialAccessToken,
      basePath: apiPath,
    });

    const apiMappings: Record<string, any> = {
      HealthApi: Api.HealthApi,
      AuthApi: Api.AuthApi,
      UserApi: Api.UserApi,
      MilestoneApi: Api.MilestoneApi,
      RedeemerApi: Api.RedeemerApi,
      PlayerApi: Api.PlayerApi,
      MigrationApi: Api.MigrationApi,
      RewardsBucketsCreationApi: Api.RewardsBucketsCreationApi,
      DareStatCreationApi: Api.DareCreationApi,
      RedeemerErrorApi: Api.RedeemerErrorApi,
      RedeemerTransactionApi: Api.RedeemerTransactionApi,
      MaintenanceWindowApi: Api.MaintenanceWindowApi,
      MaintenanceWindowCreationApi: Api.MaintenanceWindowApi,
      UserProfileApi: Api.UserProfileApi,
      LogsApi: Api.LogsApi,
      LocationsApi: Api.LocationsApi,
      StoreItemCreationApi: Api.StoreItemCreationApi,
      // This may be absent in some generated clients.
      StoreTransactionsApi: (Api as any).StoreTransactionsApi,
      ServerEnvironmentApi: Api.ServerConfigApi,
      TagTypesApi: Api.TagTypesApi,
      ScoreApi: Api.ScoreApi,
      GameContextApi: Api.GameContextApi,
      TournamentApi: Api.TournamentApi,
      JobSchedulesApi: Api.JobSchedulesApi,
      LeaderboardApi: Api.LeaderboardApi,
      ProjectApi: Api.ProjectApi,
      VersionableContentTypeApi: Api.VersionableContentTypeApi,
      ContractsApi: Api.ContractsApi,
      SidebarItemApi: Api.SidebarItemApi,
    };

    Object.keys(apiMappings).forEach((key) => {
      const ApiClass = apiMappings[key];
      if (typeof ApiClass !== "function") {
        console.warn(`[ApiManager] Missing API constructor for ${key}.`);
        return;
      }
      this.apis[key] = new ApiClass(this.config, this.config.basePath, axios);
    });

    axios.defaults.validateStatus = function (status) {
      return status >= 200 && status < 500;
    };

    axios.interceptors.request.use(async (config) => {
      this.addCustomHeaders(config);
      this.enforceJsonTypes(config);

      const requestUrl = config.url ?? "";
      const isRefreshRequest = requestUrl.includes("refresh_token");

      if (!isRefreshRequest) {
        const currentToken = this.getStoredBearerToken();

        if (currentToken) {
          this.updateAccessToken(currentToken);
        }
      }

      return config;
    });

    axios.interceptors.response.use(
      async (response) => {
        if (response.status === 401) {
          const requestUrl = response.config?.url ?? "";
          const isRefreshRequest = requestUrl.includes("refresh_token");

          // Avoid retrying refresh token requests themselves
          if (!response.config._isRetry && !isRefreshRequest) {
            response.config._isRetry = true;

            const retryResponse = await this.refreshAccessTokenIfPossible(
              response.config,
            );
            if (retryResponse) {
              return retryResponse;
            }
          }

          const authStore = useAuthStore();
          authStore.logout();
        }

        if (response.status >= 400) {
          return this.handleErrorResponse(response);
        }

        return response;
      },
      (error) => {
        return this.handleErrorResponse(error);
      },
    );
  }

  private static resolveApiBase(
    config?: { public?: { apiBase?: string }; apiBase?: string } | null,
  ): string {
    const configuredBase = config?.public?.apiBase || config?.apiBase;
    if (configuredBase && configuredBase !== "/") {
      return configuredBase;
    }

    if (typeof window !== "undefined") {
      return `${window.location.protocol}//${window.location.host}`;
    }

    return "";
  }

  private getStoredBearerToken(): string {
    return (
      localStorage.getItem("bearerToken") ||
      sessionStorage.getItem("bearerToken") ||
      ""
    );
  }

  private async refreshAccessTokenIfPossible(
    failedRequestConfig?: any,
  ): Promise<any | null> {
    if (this.refreshInFlight) {
      const refreshed = await this.refreshInFlight;
      if (!refreshed || !failedRequestConfig) {
        return null;
      }

      const newToken = this.getStoredBearerToken();
      if (!failedRequestConfig.headers) {
        failedRequestConfig.headers = {};
      }
      if (newToken) {
        failedRequestConfig.headers["Authorization"] = `Bearer ${newToken}`;
      }

      return axios(failedRequestConfig);
    }

    this.refreshInFlight = (async () => {
      const hasRefreshToken =
        !!localStorage.getItem("refreshToken") ||
        !!sessionStorage.getItem("refreshToken");

      if (!hasRefreshToken) {
        return false;
      }

      // Dynamic import avoids circular dependency between auth and api managers
      const { default: AuthManager } = await import("~/code/auth-manager");
      const authManager = AuthManager.getInstance();
      return authManager.tryRefreshToken();
    })();

    try {
      const refreshed = await this.refreshInFlight;
      if (!refreshed || !failedRequestConfig) {
        return null;
      }

      const newToken = this.getStoredBearerToken();
      if (!failedRequestConfig.headers) {
        failedRequestConfig.headers = {};
      }
      if (newToken) {
        failedRequestConfig.headers["Authorization"] = `Bearer ${newToken}`;
      }

      return axios(failedRequestConfig);
    } finally {
      this.refreshInFlight = null;
    }
  }

  private handleErrorResponse(response) {
    if (response) {
      if (response.status === 401) {
        const authStore = useAuthStore();
        authStore.logout();
      }

      const normalizedError = this.normalizeErrorResponse(response);
      //return Promise.reject(normalizedError);
      return normalizedError;
    }
  }

  normalizeErrorResponse(response: any): {
    errors: { message: string; details: string }[];
    originalError: any;
  } {
    // I don't know why, but sometimes the response is wrapped in a "response" property
    if (response.response) {
      response = response.response;
    }
    const status = response?.status ?? "Error";
    const data = response?.data ?? {};

    const title =
      typeof data.title === "string"
        ? data.title
        : typeof data.message === "string"
          ? data.message
          : typeof data.Message === "string"
            ? data.Message
            : response?.statusText || "An error occurred";

    const detail = data.detail || "";
    const traceId = data.traceId ? `Trace ID: ${data.traceId}` : "";
    const instance = data.instance ? `Instance: ${data.instance}` : "";

    const baseDetails = [`Status: ${status}`];
    if (detail) baseDetails.push(`Detail: ${detail}`);
    if (instance) baseDetails.push(instance);
    if (traceId) baseDetails.push(traceId);

    const errors: { message: string; details: string }[] = [];

    // Case 1: Validation errors (400s)
    if (
      data?.errors &&
      typeof data.errors === "object" &&
      !Array.isArray(data.errors)
    ) {
      for (const field of Object.keys(data.errors)) {
        const fieldErrors = data.errors[field];
        if (Array.isArray(fieldErrors)) {
          for (const err of fieldErrors) {
            errors.push({
              message: err,
              details: [...baseDetails, `Field: ${field}`].join("\n"),
            });
          }
        }
      }

      // Case 2: .NET structured error with Message + StackTrace
    } else if (typeof data?.Message === "string") {
      errors.push({
        message: data.Message,
        details: [...baseDetails, data.StackTrace || ""].join("\n").trim(),
      });

      if (typeof data.InnerExceptionMessage === "string") {
        errors.push({
          message: data.InnerExceptionMessage,
          details: data.InnerExceptionStackTrace || "",
        });
      }

      // Case 3: Raw string error (backend dumped string)
    } else if (typeof data === "string") {
      const lines = data.split(/\r?\n/);

      const innerMatch = data.match(
        /Inner Exception Message:\s*([\s\S]*?)\n(?:Inner Exception Stack Trace:|$)/,
      );
      const msgMatch = data.match(/Message:\s*(.+)/);

      const extracted =
        innerMatch?.[1]?.trim() ||
        msgMatch?.[1]?.trim() ||
        data.trim() ||
        "An unknown error occurred";

      errors.push({
        message: extracted,
        details: baseDetails.join("\n"),
      });

      // Case 4: Fallback
    } else {
      const raw =
        typeof data === "object" ? JSON.stringify(data, null, 2) : String(data);

      errors.push({
        message: title,
        details: [...baseDetails, raw].join("\n"),
      });
    }

    return {
      errors,
      originalError: response,
    };
  }

  public updateAccessToken(newToken: string) {
    if (newToken) {
      this.config.accessToken = newToken;
      Cookies.set("accessToken", newToken);
      return;
    }

    this.config.accessToken = "";
    Cookies.remove("accessToken");
  }

  public static getInstance(
    config?: { public?: { apiBase?: string }; apiBase?: string } | null,
  ): ApiManager {
    if (!ApiManager.instance) {
      ApiManager.instance = new ApiManager(config);
    } else if (config) {
      const resolvedBase = ApiManager.resolveApiBase(config);
      if (resolvedBase) {
        ApiManager.instance.updateBasePath(resolvedBase);
      }
    }

    return ApiManager.instance;
  }

  public updateBasePath(newBasePath: string) {
    this.config.basePath = newBasePath;
  }

  private addCustomHeaders(config) {
    config.headers["Access-Control-Allow-Origin"] = "*";
  }

  private enforceJsonTypes(config) {
    if (config.headers["Content-Type"] === "application/json" && config.data) {
      const data = JSON.parse(config.data);
      this.enforceTypes(data);
      config.data = JSON.stringify(data);
    }
  }
  private enforceTypes(obj) {
    return;
    // if (Array.isArray(obj)) {
    //   obj.forEach((item, index) => {
    //     if (typeof item === "string" && !isNaN(Number(item))) {
    //       obj[index] = Number(item);
    //     } else {
    //       this.enforceTypes(item);
    //     }
    //   });
    // } else if (typeof obj === "object" && obj !== null) {
    //   for (const key in obj) {
    //     if (Object.prototype.hasOwnProperty.call(obj, key)) {
    //       const value = obj[key];
    //       if (
    //         typeof value === "string" &&
    //         !isNaN(Number(value)) &&
    //         value !== ""
    //       ) {
    //         obj[key] = Number(value);
    //       } else {
    //         this.enforceTypes(value);
    //       }
    //     }
    //   }
    // } else {
    //   return;
    // }
  }
}

export default ApiManager;
